import React from 'react';
import { StyleSheet } from 'react-native';

import { useNavigation } from '@react-navigation/native';

import { BetrBucksPresentIcon } from '@/assets/icons/betrBucksPresentIcon';
import Plus from '@/assets/icons/plusSmall';
import { ds } from '@/components/lib/design.system';
import {
    hasTermsAndConditionsSupport,
    useTermsAndConditionsModal,
} from '@/feature/terms-and-conditions/components/TermsAndConditionModalProvider';
import { useHasUserAcceptedProductTerms } from '@/feature/terms-and-conditions/hooks/use-has-user-accepted-product-terms';
import { useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { useWallet } from '@/hooks/use-wallet';
import { withOpacity } from '@/styles/styles';
import { toLocaleCurrency } from '@/utils/numeric/currency';

import { Text, TextProps } from './TextComponent';
import { Box, TouchableBox, TouchableBoxProps } from './lib/components';

type BalanceButtonProps = {
    balance: string;
    onPress: () => void;
    showAddIcon?: boolean;
    textColor?: TextProps['color'];
    leftIcon?: React.ReactNode;
} & TouchableBoxProps;

export const BalanceButton = ({
    leftIcon,
    balance,
    onPress,
    textColor = ds.colorToken.text.primary,
    showAddIcon = true,
    ...rest
}: BalanceButtonProps) => {
    return (
        <TouchableBox
            onPress={onPress}
            minWidth={60}
            borderRadius={'r20'}
            backgroundColor={ds.colorToken.background.neutral.subtler}
            flexDirection="row"
            alignItems="center"
            px="s12"
            py={'s8'}
            {...rest}
        >
            {leftIcon}
            <Text
                color={textColor}
                style={styles.balanceButtonLabel}
                variant="labelLarge"
                ellipsizeMode="tail"
                numberOfLines={1}
            >
                {balance}
            </Text>
            {showAddIcon ? (
                <>
                    <Box height={16} width={1} mx="s12" style={styles.divider} />
                    <Plus />
                </>
            ) : null}
            <Box style={[StyleSheet.absoluteFillObject, styles.rootBorder]} />
        </TouchableBox>
    );
};

export const CashBalanceButton = () => {
    const navigation = useNavigation();
    // No need to over-fetch the wallet, wallet is updated when deposits are made, or when we access account page
    const { realMoneyTotal: total, wallet } = useWallet({ staleTimeMs: 5 * 60 * 1000 });
    const walletNotReady = !wallet;
    const { isAccepted: hasAcceptedProductTerms } = useHasUserAcceptedProductTerms();
    const { showTermsAndConditionsModal } = useTermsAndConditionsModal();
    const activeProduct = useJurisdictionStore(state => state.product);
    const hideZeroCents = Number.isInteger(total);
    const handleOnPress = () => {
        if (!hasAcceptedProductTerms && hasTermsAndConditionsSupport(activeProduct)) {
            showTermsAndConditionsModal({
                onSuccess: () => {
                    navigation.navigate('DepositStack', { screen: 'DepositScreen', params: {} });
                },
            });
            return;
        }
        navigation.navigate('DepositStack', { screen: 'DepositScreen', params: {} });
    };

    if (walletNotReady) {
        return null;
    }
    return (
        <BalanceButton
            balance={toLocaleCurrency(total, hideZeroCents, false)}
            textColor={total === 0 ? ds.colorToken.text.danger : ds.colorToken.text.primary}
            onPress={handleOnPress}
        />
    );
};

export const BetrBucksBalanceButton = () => {
    // No need to over-fetch the wallet, wallet is updated when deposits are made, or when we access account page
    const { betrBucks } = useWallet({ staleTimeMs: 5 * 60 * 1000 });

    if (betrBucks === 0 || betrBucks === undefined) {
        return null;
    }
    return (
        <BalanceButton
            leftIcon={
                <Box mr={'s8'}>
                    <BetrBucksPresentIcon />
                </Box>
            }
            balance={toLocaleCurrency(betrBucks, true, true)}
            textColor={ds.colorToken.text.brand}
            style={{ backgroundColor: withOpacity(ds.colorValue.background.brand.emphasis, 0.2) }}
            activeOpacity={1}
            flexGrow={0}
            flexShrink={1}
            showAddIcon={false}
            onPress={() => {}}
        />
    );
};

const styles = StyleSheet.create({
    rootBorder: {
        borderColor: ds.colorValue.border.subtle,
        borderRadius: 20,
        borderWidth: 1,
    },

    divider: {
        backgroundColor: ds.colorValue.border.strong,
    },
    balanceButtonLabel: {
        flexShrink: 1,
    },
});
